import { Component, OnDestroy, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription, interval } from 'rxjs';
import { ComplianceExemptionService } from '../../services/compliance-exemption.service';
import { toLocalDateOnly } from '../../utils/compliance-exemption.util';

/** Admin-only, non-dismissible alert listing companies whose trial ended and still skip T&C/KYC (spec §5.4a). */
@Component({
  selector: 'app-trial-ended-alert',
  templateUrl: './trial-ended-alert.component.html',
  styleUrls: ['./trial-ended-alert.component.css'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateY(-16px)', opacity: 0 }),
        animate('320ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'translateY(-16px)', opacity: 0 }))]),
    ]),
  ],
})
export class TrialEndedAlertComponent implements OnInit, OnDestroy {
  items: any[] = [];
  isAdmin = false;
  private subs: Subscription[] = [];
  private static readonly POLL_MS = 5 * 60 * 1000;

  constructor(private api: ComplianceExemptionService, private router: Router, private cookies: CookieService) {}

  ngOnInit(): void {
    this.isAdmin = (this.cookies.get('userRole') || '').trim().toLowerCase() === 'admin';
    if (!this.isAdmin) { return; }
    this.load();
    this.subs.push(interval(TrialEndedAlertComponent.POLL_MS).subscribe(() => this.load()));
    this.subs.push(this.api.changed$.subscribe(() => this.load()));
  }

  ngOnDestroy(): void { this.subs.forEach((s) => s.unsubscribe()); }

  async load(): Promise<void> {
    try {
      const r: any = await this.api.endedTrials();
      this.items = Array.isArray(r?.items) ? r.items : [];
    } catch {
      // Keep the last known list; an API blip must not hide or flash the alert.
    }
  }

  review(item: any): void {
    if (!item?.primaryUserId) { return; }
    const url = item.partyType === 'Supplier'
      ? `/settings/supplierProfile/${item.primaryUserId}/${item.companyId}`
      : `/settings/buyer/profile/${item.primaryUserId}/${item.companyId}`;
    this.router.navigateByUrl(url);
  }

  endedOn(item: any): Date | null {
    return toLocalDateOnly(item?.endsAt ?? null);
  }
}
